import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { IconButton, Text } from "@carbon6/c6-frontend-design";
import { useState, type ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { MemoDetailModal } from "./MemoDetailModal";
import { truncateMemo } from "./memoFormatters";
import { useMemoController } from "./useMemoController";
import styles from "./MemoStrip.module.css";

// 상세 화면 하단의 메모 스트립이다. 최근 메모 2개를 짧게 표시하고, 클릭/버튼으로 전체 모달을 연다.
// 모달에서 작성/삭제한 내용은 모달을 닫을 때 reload 로 스트립에 반영한다.
export function MemoStrip({
  datasetUuid,
  canMutate = true,
}: {
  datasetUuid: string;
  canMutate?: boolean;
}): ReactNode {
  const controller = useMemoController(datasetUuid, { canMutate });
  const [open, setOpen] = useState(false);
  const { state } = controller;

  const memos = state.status === "success" ? state.data : [];
  const recent = memos.slice(0, 2);
  const countSuffix = state.status === "success" ? ` (${memos.length})` : "";

  return (
    <section className={styles.strip} aria-label="메모">
      <div className={styles.header}>
        <Text variant={hatcheryTextModes["caption-bold"]}>메모{countSuffix}</Text>
        <IconButton text="메모 보기" size="sm" variant="surface" onClick={() => setOpen(true)} />
      </div>
      <button type="button" className={styles.recent} onClick={() => setOpen(true)}>
        {state.status === "loading" || state.status === "idle" ? (
          <span className={styles.muted}>메모를 불러오고 있습니다.</span>
        ) : state.status === "error" ? (
          <span className={styles.muted}>{state.message}</span>
        ) : recent.length === 0 ? (
          <span className={styles.muted}>
            {controller.canMutate
              ? "아직 작성된 메모가 없습니다. 클릭해 메모를 작성해 주세요."
              : "아직 메모가 없습니다."}
          </span>
        ) : (
          recent.map((memo) => (
            <span key={memo.uuid} className={styles.recentItem}>
              <span className={styles.author}>{memo.author_name}</span>
              <span className={styles.content}>{truncateMemo(memo.content, 60)}</span>
              <span className={styles.date}>{formatDateTime(parseUtcMs(memo.created_at))}</span>
            </span>
          ))
        )}
      </button>
      <MemoDetailModal
        open={open}
        datasetUuid={datasetUuid}
        canMutate={canMutate}
        onClose={() => {
          setOpen(false);
          controller.reload();
        }}
      />
    </section>
  );
}
